import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { useAuth, API } from '../App';
import { Plus, Search, X, MoreVertical, Calendar as CalendarIcon, Edit, RotateCw, DollarSign, PlayCircle, CheckCircle2, Ban, History, Trash2, ChevronDown, ChevronUp, GripVertical, Undo2, MapPin, Printer, MessageCircle, Copy, CalendarCheck2 } from 'lucide-react';
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AgendaRegisterDialog from './AgendaRegisterDialog';
import { todayMx, shiftDateStr } from '../lib/dateUtils';

const ESTADOS = [
  { value: 'pendiente',  label: 'Pendiente',  row: 'bg-amber-100 dark:bg-amber-950/30',      cell: 'bg-amber-200 text-amber-900' },
  { value: 'confirmado', label: 'Confirmado', row: 'bg-blue-100 dark:bg-blue-950/30',        cell: 'bg-blue-200 text-blue-900' },
  { value: 'en_proceso', label: 'En proceso', row: 'bg-orange-100 dark:bg-orange-950/30',    cell: 'bg-orange-200 text-orange-900' },
  { value: 'finalizado', label: 'Finalizado', row: 'bg-green-100 dark:bg-green-950/30',      cell: 'bg-green-200 text-green-900' },
  { value: 'reagendado', label: 'Reagendado', row: 'bg-purple-100 dark:bg-purple-950/30',    cell: 'bg-purple-200 text-purple-900' },
  { value: 'cancelado',  label: 'Cancelado',  row: 'bg-slate-100 opacity-60 dark:bg-slate-800/50', cell: 'bg-slate-300 text-slate-800' },
];
const estByVal = Object.fromEntries(ESTADOS.map(e => [e.value, e]));

// Colores por tipo de registro (record_type) — anulan el color de estado para tipos especiales
const TIPO_ROW = {
  disponible: 'bg-emerald-100 dark:bg-emerald-950/40 border-l-4 border-emerald-500',
  apoyo:      'bg-purple-100 dark:bg-purple-950/40 border-l-4 border-purple-500',
  interno:    'bg-orange-100 dark:bg-orange-950/40 border-l-4 border-orange-500',
};
const TIPO_BADGE = {
  trabajo:    null,
  disponible: { label: 'Disponible', cls: 'bg-emerald-500 text-white' },
  apoyo:      { label: 'Apoyo',      cls: 'bg-purple-500 text-white' },
  interno:    { label: 'Interno',    cls: 'bg-orange-500 text-white' },
};
const TIPOS = [
  { value: 'trabajo',    label: 'Trabajo cliente' },
  { value: 'disponible', label: 'Disponible' },
  { value: 'apoyo',      label: 'Apoyo' },
  { value: 'interno',    label: 'Actividad interna' },
];
const DEFAULT_DURATION = 60;

const fmt = (n) => `$${(parseFloat(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function emptyOrder() {
  const today = todayMx();
  return { service_date: today, start_time: '09:00', end_time: '10:00', client_name: '', phones: [''], address: '', address_reference: '', service_type: '', description: '', technician_id: '', technician_name: '', assigned_by: '', assigned_by_id: '', quoted_cost: 0, anticipo: 0, contact_medium: '', internal_comments: '', status: 'pendiente' };
}

// Utilidades de hora
const timeToMin = (t) => {
  if (!t || typeof t !== 'string') return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};
const minToTime = (mins) => {
  const m = Math.max(0, Math.min(23 * 60 + 59, Math.round(mins)));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};
const getDurationMin = (s) => {
  if (s.duration_minutes && s.duration_minutes > 0) return s.duration_minutes;
  const st = timeToMin(s.start_time);
  const et = timeToMin(s.end_time);
  const diff = et - st;
  return diff > 0 ? diff : DEFAULT_DURATION;
};

/**
 * Recalcula horarios encadenados para un grupo (mismo técnico + fecha),
 * usando el start_time del PRIMER trabajo como ancla.
 * Devuelve nuevo array con start_time/end_time actualizados.
 */
function recalcGroupTimes(group) {
  if (!group.length) return group;
  const first = group[0];
  let anchor = timeToMin(first.start_time || '09:00') || 9 * 60;
  return group.map((s, idx) => {
    const dur = getDurationMin(s);
    const st = idx === 0 ? anchor : anchor;
    const et = st + dur;
    anchor = et;
    return { ...s, start_time: minToTime(st), end_time: minToTime(et), duration_minutes: dur };
  });
}

/**
 * Agrupa servicios por técnico+fecha y aplica orden estable por (agenda_order, start_time).
 */
function groupServices(services) {
  const groups = {};
  services.forEach(s => {
    const key = `${s.service_date || 'sin-fecha'}__${s.technician_id || 'sin-tecnico'}`;
    if (!groups[key]) groups[key] = { key, service_date: s.service_date, technician_id: s.technician_id, technician_name: s.technician_name || 'Sin asignar', items: [] };
    groups[key].items.push(s);
  });
  Object.values(groups).forEach(g => {
    g.items.sort((a, b) => {
      const ao = a.agenda_order ?? 9999;
      const bo = b.agenda_order ?? 9999;
      if (ao !== bo) return ao - bo;
      return timeToMin(a.start_time) - timeToMin(b.start_time);
    });
  });
  return Object.values(groups).sort((a, b) => {
    if ((a.service_date || '') !== (b.service_date || '')) return (a.service_date || '').localeCompare(b.service_date || '');
    return (a.technician_name || '').localeCompare(b.technician_name || '');
  });
}

// ============ Celda editable inline ============
function EditableCell({ value, onSave, type = 'text', options, placeholder, className = '', multiline = false, testid, disabled = false }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  useEffect(() => { setVal(value ?? ''); }, [value]);

  const commit = async () => {
    if ((val ?? '') === (value ?? '')) { setEditing(false); return; }
    try {
      await onSave(val);
      setEditing(false);
    } catch {
      setVal(value ?? '');
      setEditing(false);
    }
  };
  const cancel = () => { setVal(value ?? ''); setEditing(false); };

  if (disabled) {
    return <div className={`px-1 py-0.5 ${className}`}>{value || <span className="text-muted-foreground">-</span>}</div>;
  }

  if (!editing) {
    let displayValue = value;
    if (type === 'select' && options) {
      const opt = options.find(o => String(o.value) === String(value ?? ''));
      displayValue = opt ? opt.label : (value || '');
      if (displayValue === '— Sin asignar —' || displayValue === '—') displayValue = '';
    }
    return (
      <div
        className={`px-1 py-0.5 rounded cursor-text hover:bg-white/40 dark:hover:bg-white/10 ${multiline ? 'whitespace-pre-wrap' : 'truncate'} ${className}`}
        onClick={() => setEditing(true)}
        data-testid={testid}
        title={multiline ? undefined : (displayValue || '')}
      >
        {displayValue || <span className="text-muted-foreground">{placeholder || '—'}</span>}
      </div>
    );
  }

  if (type === 'select') {
    return (
      <select
        autoFocus
        className="h-7 w-full rounded border bg-background px-1 text-xs"
        value={val || ''}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        data-testid={testid ? `${testid}-input` : undefined}
      >
        {(options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }

  if (multiline) {
    return (
      <Textarea
        autoFocus
        rows={3}
        className="text-xs min-h-[60px]"
        value={val || ''}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Escape') cancel(); }}
        data-testid={testid ? `${testid}-input` : undefined}
      />
    );
  }

  return (
    <Input
      autoFocus
      type={type}
      className="h-7 text-xs"
      value={val ?? ''}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } else if (e.key === 'Escape') cancel(); }}
      placeholder={placeholder}
      data-testid={testid ? `${testid}-input` : undefined}
    />
  );
}

// ============ Fila arrastrable ============
function AgendaRow({ s, technicians, expanded, toggleExpand, onFieldSave, actions, isAdmin, apoyoLegends }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const est = estByVal[s.status] || estByVal.pendiente;
  const rt = s.record_type || 'trabajo';
  const tipoBadge = TIPO_BADGE[rt];
  const rowClass = TIPO_ROW[rt] || est.row;
  const acum = s.anticipo_acumulado || s.anticipo || 0;
  const costo = s.monto_total || s.quoted_cost || 0;
  const restante = Math.max(0, parseFloat(costo || 0) - parseFloat(acum || 0));
  const tienePendientes = (s.comentario_cierre || '').includes('⚠️');
  const isFinalized = s.status === 'finalizado' || s.status === 'cancelado';
  const commentText = s.internal_comments || s.comentario_cierre || '';
  const isExpanded = expanded[s.id];
  const durMin = s.duration_minutes || (parseInt(timeToMin(s.end_time) - timeToMin(s.start_time)) || DEFAULT_DURATION);
  const apoyoLegend = apoyoLegends?.[s.id];
  const isDisponible = rt === 'disponible';
  const isApoyo = rt === 'apoyo';
  const isInterno = rt === 'interno';

  return (
    <tr ref={setNodeRef} style={style} className={`border-t ${rowClass}`} data-testid={`agenda-row-${s.folio}`}>
      <td className="px-1 py-1.5 w-6">
        <button ref={setNodeRef} type="button" className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground" {...attributes} {...listeners} data-testid={`agenda-drag-${s.folio}`} aria-label="Reordenar">
          <GripVertical className="h-4 w-4" />
        </button>
      </td>
      <td className="px-2 py-1.5 font-mono">
        {s.folio}
        {tipoBadge && <span className={`ml-1 px-1 rounded text-[9px] font-semibold ${tipoBadge.cls}`}>{tipoBadge.label}</span>}
      </td>
      <td className="px-2 py-1.5 whitespace-nowrap w-[130px]">
        <EditableCell value={s.service_date} type="date" onSave={(v) => onFieldSave(s.id, { service_date: v })} disabled={isFinalized} testid={`cell-date-${s.folio}`} />
        {s.status === 'reagendado' && s.eventos && s.eventos.length > 1 && (<span className="block text-[10px] text-purple-700 dark:text-purple-300">→ reagendado</span>)}
      </td>
      <td className="px-2 py-1.5 whitespace-nowrap w-[160px]">
        <div className="flex gap-1">
          <EditableCell value={s.start_time} type="time" onSave={(v) => onFieldSave(s.id, { start_time: v })} disabled={isFinalized} testid={`cell-start-${s.folio}`} className="w-[70px]" />
          <span className="text-muted-foreground">-</span>
          <EditableCell value={s.end_time} type="time" onSave={(v) => onFieldSave(s.id, { end_time: v })} disabled={isFinalized} testid={`cell-end-${s.folio}`} className="w-[70px]" />
        </div>
        <div className="text-[9px] text-muted-foreground">{durMin} min</div>
      </td>
      <td className="px-2 py-1.5 min-w-[140px]">
        <EditableCell
          value={s.technician_id || ''}
          type="select"
          options={(() => {
            const base = [{ value: '', label: '— Sin asignar —' }, ...technicians.map(t => ({ value: t.id, label: t.name }))];
            if (s.technician_id && !technicians.find(t => t.id === s.technician_id)) {
              base.push({ value: s.technician_id, label: s.technician_name || 'Técnico anterior' });
            }
            return base;
          })()}
          onSave={(v) => { const t = technicians.find(x => x.id === v); return onFieldSave(s.id, { technician_id: v || null, technician_name: t?.name || '' }); }}
          disabled={isFinalized}
          testid={`cell-tech-${s.folio}`}
        />
        {s.ayudante_name && <div className="text-[9px] text-muted-foreground">+ Ayudante: {s.ayudante_name}</div>}
      </td>
      <td className="px-2 py-1.5 text-muted-foreground text-[11px]">{s.assigned_by || '-'}</td>
      <td className="px-2 py-1.5 min-w-[140px]">
        {isDisponible ? (
          <span className="text-emerald-800 dark:text-emerald-200 font-semibold text-xs">🟢 Disponible para agendar</span>
        ) : isApoyo ? (
          <span className="text-purple-800 dark:text-purple-200 font-semibold text-xs">🤝 Apoyo a {s.apoyo_a_tecnico_name || '—'}</span>
        ) : isInterno ? (
          <span className="text-orange-800 dark:text-orange-200 font-semibold text-xs">⛔ {s.motivo_interno || 'Actividad interna'}</span>
        ) : (
          <EditableCell value={s.service_type} onSave={(v) => onFieldSave(s.id, { service_type: v })} disabled={isFinalized} testid={`cell-service-${s.folio}`} />
        )}
        {s.description && (
          <div className="text-[10px] text-muted-foreground mt-0.5">
            <EditableCell value={s.description} multiline onSave={(v) => onFieldSave(s.id, { description: v })} disabled={isFinalized} testid={`cell-desc-${s.folio}`} className={isExpanded ? '' : 'line-clamp-2'} />
          </div>
        )}
        {apoyoLegend && !isApoyo && (
          <div className="text-[10px] text-purple-700 dark:text-purple-300 mt-0.5 font-medium">🤝 Recibirá apoyo de {apoyoLegend.tech_name} de {apoyoLegend.start} a {apoyoLegend.end}</div>
        )}
      </td>
      <td className="px-2 py-1.5 font-medium min-w-[140px]">
        {rt === 'trabajo' ? (
          <>
            <EditableCell value={s.client_name} onSave={(v) => onFieldSave(s.id, { client_name: v })} disabled={isFinalized} testid={`cell-client-${s.folio}`} />
            {s.status === 'reagendado' && s.leyenda_reagenda && (<div className="text-[10px] text-purple-700 dark:text-purple-300 font-semibold">{s.leyenda_reagenda}</div>)}
            {s.reagendado_desde_folio && (<div className="text-[10px] text-blue-700 dark:text-blue-300">↩ Viene de {s.reagendado_desde_folio} ({s.fecha_original} {s.hora_original || ''})</div>)}
          </>
        ) : (
          <span className="text-muted-foreground text-[11px]">—</span>
        )}
      </td>
      <td className="px-2 py-1.5 min-w-[120px]">
        {rt === 'trabajo' ? (
          <EditableCell
            value={(s.phones || [])[0] || ''}
            onSave={(v) => onFieldSave(s.id, { phones: [v, ...(s.phones || []).slice(1)].filter(Boolean) })}
            disabled={isFinalized}
            testid={`cell-phone-${s.folio}`}
          />
        ) : <span className="text-muted-foreground text-[11px]">—</span>}
      </td>
      <td className="px-2 py-1.5 min-w-[160px]">
        {rt === 'trabajo' ? (
          <EditableCell value={s.address} onSave={(v) => onFieldSave(s.id, { address: v })} disabled={isFinalized} testid={`cell-address-${s.folio}`} />
        ) : <span className="text-muted-foreground text-[11px]">—</span>}
      </td>
      <td className="px-2 py-1.5 min-w-[110px]">
        {rt === 'trabajo' ? (
          <EditableCell
            value={s.contact_medium || ''}
            type="select"
            options={[{ value: '', label: '—' }, { value: 'Whatsapp', label: 'WhatsApp' }, { value: 'Llamada', label: 'Llamada' }, { value: 'Facebook', label: 'Facebook' }, { value: 'Referido', label: 'Referido' }, { value: 'Presencial', label: 'Presencial' }, { value: 'Otro', label: 'Otro' }]}
            onSave={(v) => onFieldSave(s.id, { contact_medium: v })}
            disabled={isFinalized}
            testid={`cell-medium-${s.folio}`}
          />
        ) : <span className="text-muted-foreground text-[11px]">—</span>}
      </td>
      <td className="px-2 py-1.5 text-right min-w-[90px]">
        {rt === 'trabajo' ? (
          <EditableCell
            value={parseFloat(costo || 0).toFixed(2)}
            type="number"
            onSave={(v) => onFieldSave(s.id, { quoted_cost: parseFloat(v) || 0 })}
            disabled={isFinalized}
            testid={`cell-cost-${s.folio}`}
            className="text-right"
          />
        ) : <span className="text-muted-foreground text-[11px]">—</span>}
      </td>
      <td className="px-2 py-1.5 text-right text-emerald-700">{rt === 'trabajo' ? fmt(acum) : '—'}</td>
      <td className={`px-2 py-1.5 text-right font-medium ${restante > 0 ? 'text-orange-700' : 'text-slate-500'}`}>{rt === 'trabajo' ? fmt(restante) : '—'}</td>
      <td className="px-2 py-1.5 min-w-[120px]">
        <EditableCell
          value={s.status || 'pendiente'}
          type="select"
          options={ESTADOS.map(e => ({ value: e.value, label: e.label }))}
          onSave={(v) => onFieldSave(s.id, { status: v })}
          testid={`cell-status-${s.folio}`}
        />
        {tienePendientes && <span className="block text-[9px] text-red-700 font-semibold">⚠️ Con pendientes</span>}
      </td>
      <td className="px-2 py-1.5 font-mono">{s.folio_nota || '-'}</td>
      <td className="px-2 py-1.5 min-w-[180px]">
        <EditableCell
          value={commentText}
          multiline
          onSave={(v) => onFieldSave(s.id, { internal_comments: v })}
          testid={`cell-comments-${s.folio}`}
          className={isExpanded ? '' : 'line-clamp-2'}
        />
        {commentText.length > 60 && (
          <button type="button" onClick={() => toggleExpand(s.id)} className="text-blue-600 text-[10px] flex items-center gap-0.5 mt-0.5">
            {isExpanded ? <><ChevronUp className="h-3 w-3" />Contraer</> : <><ChevronDown className="h-3 w-3" />Ver más</>}
          </button>
        )}
      </td>
      <td className="px-1 py-1.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`agenda-actions-${s.id}`}><MoreVertical className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {isDisponible && (
              <DropdownMenuItem onClick={() => actions.openConvert(s)} className="text-emerald-700" data-testid={`agenda-convert-${s.id}`}><CalendarCheck2 className="h-4 w-4 mr-2" />Convertir a trabajo</DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => actions.openEdit(s)}><Edit className="h-4 w-4 mr-2" />Editar (modal)</DropdownMenuItem>
            {rt === 'trabajo' && <DropdownMenuItem onClick={() => actions.openResched(s)} disabled={isFinalized}><RotateCw className="h-4 w-4 mr-2" />Reagendar</DropdownMenuItem>}
            {rt === 'trabajo' && <DropdownMenuItem onClick={() => actions.openAnticipo(s)} disabled={isFinalized}><DollarSign className="h-4 w-4 mr-2" />Registrar abono</DropdownMenuItem>}
            <DropdownMenuSeparator />
            {rt === 'trabajo' && s.status === 'pendiente' && <DropdownMenuItem onClick={() => actions.confirmWork(s)}>Marcar confirmado</DropdownMenuItem>}
            {rt === 'trabajo' && (s.status === 'pendiente' || s.status === 'confirmado') && <DropdownMenuItem onClick={() => actions.startWork(s)}><PlayCircle className="h-4 w-4 mr-2" />Iniciar trabajo</DropdownMenuItem>}
            {rt === 'trabajo' && <DropdownMenuItem onClick={() => actions.openFinalize(s)} disabled={isFinalized} data-testid={`agenda-finalize-${s.id}`}><CheckCircle2 className="h-4 w-4 mr-2 text-emerald-600" />Finalizar</DropdownMenuItem>}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => actions.openHistory(s)}><History className="h-4 w-4 mr-2" />Ver historial</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => actions.openCancel(s)} disabled={s.status === 'cancelado'} className="text-orange-600"><Ban className="h-4 w-4 mr-2" />Cancelar</DropdownMenuItem>
            {isAdmin && (
              <DropdownMenuItem onClick={() => actions.deleteForever(s)} className="text-destructive" data-testid={`agenda-delete-${s.id}`}><Trash2 className="h-4 w-4 mr-2" />Eliminar definitivamente</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

// ============ Grupo (droppable) por técnico/fecha ============
function AgendaGroup({ group, technicians, expanded, toggleExpand, onFieldSave, actions, isAdmin, onOpenRuta, apoyoLegends }) {
  const ids = useMemo(() => group.items.map(x => x.id), [group.items]);
  const activos = group.items.filter(x => x.status !== 'cancelado' && x.status !== 'finalizado' && (x.record_type || 'trabajo') === 'trabajo' && x.address);
  return (
    <>
      <tr className="bg-slate-200 dark:bg-slate-800/60 font-semibold text-xs">
        <td colSpan={18} className="px-3 py-1.5" data-testid={`agenda-group-${group.service_date}-${group.technician_id || 'none'}`}>
          <div className="flex items-center justify-between gap-3">
            <span>📅 {group.service_date || 'Sin fecha'} · 👷 {group.technician_name} · <span className="text-muted-foreground font-normal">{group.items.length} registros</span></span>
            {activos.length > 0 && (
              <Button type="button" size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={() => onOpenRuta(group)} data-testid={`agenda-ruta-${group.service_date}-${group.technician_id || 'none'}`}>
                <MapPin className="h-3 w-3" />Ruta del día
              </Button>
            )}
          </div>
        </td>
      </tr>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {group.items.map(s => (
          <AgendaRow key={s.id} s={s} technicians={technicians} expanded={expanded} toggleExpand={toggleExpand} onFieldSave={onFieldSave} actions={actions} isAdmin={isAdmin} apoyoLegends={apoyoLegends} />
        ))}
      </SortableContext>
    </>
  );
}

export default function AgendaTrabajos() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [expanded, setExpanded] = useState({});
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelForm, setCancelForm] = useState({ motivo: '', fecha: todayMx(), comentario: '', devolver: false, monto_devuelto: 0 });
  const [services, setServices] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState(todayMx());
  const [filterMode, setFilterMode] = useState('day');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterTec, setFilterTec] = useState('all');
  const [filterEst, setFilterEst] = useState('all');
  const [filterService, setFilterService] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState('all');

  const [createOpen, setCreateOpen] = useState(false);
  const openCreate = () => setCreateOpen(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedTarget, setReschedTarget] = useState(null);
  const [reschedForm, setReschedForm] = useState({ nueva_fecha: '', hora_inicio: '09:00', hora_fin: '10:00', nuevo_tecnico_id: '', motivo: '', observaciones_dia_actual: '' });
  const [anticipoOpen, setAnticipoOpen] = useState(false);
  const [anticipoTarget, setAnticipoTarget] = useState(null);
  const [anticipoForm, setAnticipoForm] = useState({ costo_total: 0, anticipo: 0, fecha: '', medio_pago: 'efectivo', comentario: '' });
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizeTarget, setFinalizeTarget] = useState(null);
  const [finalizeForm, setFinalizeForm] = useState({ folio_nota: '', monto_cobrado: 0, tiene_pendientes: false, pendientes_descripcion: '', comentario_cierre: '' });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [rutaOpen, setRutaOpen] = useState(false);
  const [rutaGroup, setRutaGroup] = useState(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertTarget, setConvertTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const undoSnapshotRef = useRef(null);
  const [undoAvailable, setUndoAvailable] = useState(false);
  const [conflictDialog, setConflictDialog] = useState(null); // { movedItem, conflict, slots }

  const fetchServices = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterSearch) params.append('search', filterSearch);
      if (filterEst !== 'all') params.append('status', filterEst);
      if (filterTec !== 'all') params.append('technician_id', filterTec);
      if (filterMode === 'day') {
        params.append('date_from', filterDate);
        params.append('date_to', filterDate);
        params.append('incluir_eventos_fecha', 'true');
      } else if (filterMode === 'range' && filterFrom && filterTo) {
        params.append('date_from', filterFrom);
        params.append('date_to', filterTo);
      }
      const r = await axios.get(`${API}/services?${params}`);
      let data = r.data;
      if (filterService) {
        const s = filterService.toLowerCase();
        data = data.filter(x => (x.service_type || '').toLowerCase().includes(s));
      }
      if (filterTipo !== 'all') {
        data = data.filter(x => (x.record_type || 'trabajo') === filterTipo);
      }
      setServices(data);
    } catch { toast.error('Error al cargar agenda'); }
    finally { setLoading(false); }
  }, [filterSearch, filterEst, filterTec, filterMode, filterDate, filterFrom, filterTo, filterService, filterTipo]);

  useEffect(() => {
    axios.get(`${API}/technicians?tipo=tecnico`).then(r => setTechnicians(r.data.filter(t => t.is_active !== false))).catch(() => {});
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const shiftDay = (delta) => {
    setFilterDate(shiftDateStr(filterDate, delta));
  };

  const groups = useMemo(() => groupServices(services), [services]);

  // ---- Inline field save ----
  const onFieldSave = useCallback(async (id, patch) => {
    try {
      await axios.put(`${API}/services/${id}`, patch);
      setServices(prev => prev.map(x => x.id === id ? { ...x, ...patch } : x));
      toast.success('Guardado', { duration: 1200 });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof detail === 'object' && detail?.conflict) {
        const movedItem = services.find(x => x.id === id);
        if (movedItem) {
          const merged = { ...movedItem, ...patch };
          try {
            const rs = await axios.get(`${API}/agenda/available-slots`, { params: { technician_id: merged.technician_id, service_date: merged.service_date, duration_min: merged.duration_minutes || 60, exclude_id: id } });
            setConflictDialog({
              movedItem: merged,
              conflict: { conflict_id: detail.conflict.id, conflict_folio: detail.conflict.folio, conflict_start: detail.conflict.start_time, conflict_end: detail.conflict.end_time, conflict_client: detail.conflict.client_name || detail.conflict.motivo_interno || '' },
              slots: rs.data.available_slots || [],
              ocupados: rs.data.ocupados || [],
            });
          } catch { /* ignore */ }
        }
        toast.error(`Conflicto: ${detail.conflict.client_name || detail.conflict.motivo_interno || detail.conflict.record_type} · ${detail.conflict.start_time}-${detail.conflict.end_time}`, { duration: 5000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Error al guardar'));
      }
      throw err;
    }
  }, [services]);

  // ---- Drag and drop handler ----
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeItem = services.find(s => s.id === active.id);
    const overItem = services.find(s => s.id === over.id);
    if (!activeItem || !overItem) return;

    // snapshot para deshacer
    undoSnapshotRef.current = services.map(s => ({ id: s.id, service_date: s.service_date, technician_id: s.technician_id, technician_name: s.technician_name, start_time: s.start_time, end_time: s.end_time, agenda_order: s.agenda_order, duration_minutes: s.duration_minutes }));

    const sameGroup = (activeItem.service_date === overItem.service_date) && ((activeItem.technician_id || '') === (overItem.technician_id || ''));

    // Reconstruir grupos
    const grouped = groupServices(services);
    let newAll = [...services];

    if (sameGroup) {
      const gKey = `${activeItem.service_date}__${activeItem.technician_id || 'sin-tecnico'}`;
      const g = grouped.find(x => x.key === gKey);
      if (!g) return;
      const oldIdx = g.items.findIndex(x => x.id === active.id);
      const newIdx = g.items.findIndex(x => x.id === over.id);
      const reordered = arrayMove(g.items, oldIdx, newIdx);
      const recalced = recalcGroupTimes(reordered).map((s, i) => ({ ...s, agenda_order: i }));
      newAll = services.map(s => {
        const r = recalced.find(x => x.id === s.id);
        return r ? r : s;
      });
    } else {
      // Mover entre grupos: cambia service_date y/o technician
      const targetDate = overItem.service_date;
      const targetTechId = overItem.technician_id || null;
      const targetTechName = overItem.technician_name || '';
      const targetKey = `${targetDate}__${targetTechId || 'sin-tecnico'}`;
      const oldKey = `${activeItem.service_date}__${activeItem.technician_id || 'sin-tecnico'}`;

      const targetGroup = grouped.find(x => x.key === targetKey);
      const oldGroup = grouped.find(x => x.key === oldKey);
      const overIdx = targetGroup ? targetGroup.items.findIndex(x => x.id === over.id) : 0;

      const movedItem = { ...activeItem, service_date: targetDate, technician_id: targetTechId, technician_name: targetTechName };

      // Nuevos items del grupo destino con el movedItem insertado
      const newTargetItems = targetGroup ? [...targetGroup.items] : [];
      newTargetItems.splice(overIdx, 0, movedItem);
      // Remover del target si ya existía (por si acaso)
      const seen = new Set();
      const uniqueTarget = newTargetItems.filter(x => { if (seen.has(x.id)) return false; seen.add(x.id); return true; });
      const recalcedTarget = recalcGroupTimes(uniqueTarget).map((s, i) => ({ ...s, agenda_order: i }));

      // Nuevos items del grupo origen sin el movido
      const newOldItems = oldGroup ? oldGroup.items.filter(x => x.id !== activeItem.id) : [];
      const recalcedOld = recalcGroupTimes(newOldItems).map((s, i) => ({ ...s, agenda_order: i }));

      newAll = services.map(s => {
        const inT = recalcedTarget.find(x => x.id === s.id);
        if (inT) return inT;
        const inO = recalcedOld.find(x => x.id === s.id);
        if (inO) return inO;
        return s;
      });
    }

    // Optimistic UI
    setServices(newAll);

    // Preparar payload batch (solo cambios)
    const changed = newAll.filter(s => {
      const prev = services.find(x => x.id === s.id);
      if (!prev) return false;
      return (prev.service_date !== s.service_date) ||
             ((prev.technician_id || null) !== (s.technician_id || null)) ||
             (prev.start_time !== s.start_time) ||
             (prev.end_time !== s.end_time) ||
             ((prev.agenda_order ?? -1) !== (s.agenda_order ?? -1));
    });

    if (!changed.length) return;

    try {
      await axios.put(`${API}/services/schedule-batch`, {
        items: changed.map(s => ({ id: s.id, service_date: s.service_date, technician_id: s.technician_id, technician_name: s.technician_name, start_time: s.start_time, end_time: s.end_time, duration_minutes: s.duration_minutes, agenda_order: s.agenda_order })),
      });
      setUndoAvailable(true);
      toast.success(`Agenda actualizada (${changed.length})`, {
        action: { label: 'Deshacer', onClick: () => undoLastMove() },
        duration: 6000,
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof detail === 'object' && detail?.conflicts) {
        const c = detail.conflicts[0] || {};
        // moved item = el que producía conflicto (item_id)
        const movedItem = newAll.find(x => x.id === c.item_id) || activeItem;
        // Consultar huecos libres del técnico destino
        try {
          const rs = await axios.get(`${API}/agenda/available-slots`, { params: { technician_id: movedItem.technician_id, service_date: movedItem.service_date, duration_min: movedItem.duration_minutes || 60, exclude_id: movedItem.id } });
          setConflictDialog({ movedItem, conflict: c, slots: rs.data.available_slots || [], ocupados: rs.data.ocupados || [] });
        } catch {
          setConflictDialog({ movedItem, conflict: c, slots: [], ocupados: [] });
        }
        toast.error(`Conflicto con ${c.conflict_client || c.conflict_folio} (${c.conflict_start}-${c.conflict_end}).`);
      } else {
        toast.error('Error al guardar cambios. Revirtiendo.');
      }
      fetchServices();
    }
  }, [services, fetchServices]);

  const undoLastMove = useCallback(async () => {
    if (!undoSnapshotRef.current) return;
    const snap = undoSnapshotRef.current;
    try {
      await axios.put(`${API}/services/schedule-batch`, { items: snap });
      toast.success('Cambios revertidos');
      undoSnapshotRef.current = null;
      setUndoAvailable(false);
      fetchServices();
    } catch { toast.error('No se pudo deshacer'); }
  }, [fetchServices]);

  // ===== CREATE handled by AgendaRegisterDialog =====

  // ===== EDIT =====
  const openEdit = (s) => { setEditTarget({ ...s, phones: (s.phones || ['']).length ? s.phones : [''] }); setEditOpen(true); };
  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editTarget, phones: (editTarget.phones || []).filter(Boolean) };
      await axios.put(`${API}/services/${editTarget.id}`, payload);
      toast.success('Actualizado');
      setEditOpen(false);
      fetchServices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const openResched = (s) => {
    setReschedTarget(s);
    const total = parseFloat(s.monto_total || s.quoted_cost || 0);
    const acum = parseFloat(s.anticipo_acumulado || s.anticipo || 0);
    setReschedForm({ nueva_fecha: '', hora_inicio: s.start_time || '09:00', hora_fin: s.end_time || '10:00', nuevo_tecnico_id: '', motivo: '', observaciones_dia_actual: '', service_type: s.service_type || '', description: s.description || '', costo_total: total, anticipo_extra: 0, anticipo_actual: acum, pendientes: '' });
    setReschedOpen(true);
  };
  const submitResched = async (e) => {
    e.preventDefault();
    if (!reschedForm.nueva_fecha) { toast.error('Nueva fecha requerida'); return; }
    try {
      const prevPendientes = reschedForm.pendientes ? `📌 Pendientes: ${reschedForm.pendientes}` : '';
      await axios.post(`${API}/services/${reschedTarget.id}/reagendar-clone`, {
        nueva_fecha: reschedForm.nueva_fecha,
        hora_inicio: reschedForm.hora_inicio,
        hora_fin: reschedForm.hora_fin,
        nuevo_tecnico_id: reschedForm.nuevo_tecnico_id || null,
        service_type: reschedForm.service_type,
        description: reschedForm.description,
        costo_total_cotizado: parseFloat(reschedForm.costo_total) || 0,
        anticipo: parseFloat(reschedForm.anticipo_extra) || 0,
        motivo: reschedForm.motivo,
        observaciones_dia_actual: reschedForm.observaciones_dia_actual,
        comentarios_nuevos: prevPendientes,
      });
      toast.success('Reagendado — se creó nuevo registro y el original quedó como historial');
      setReschedOpen(false);
      fetchServices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const openAnticipo = (s) => {
    setAnticipoTarget(s);
    setAnticipoForm({ costo_total: s.monto_total || s.quoted_cost || 0, anticipo: 0, fecha: todayMx(), medio_pago: 'efectivo', comentario: '' });
    setAnticipoOpen(true);
  };
  const submitAnticipo = async (e) => {
    e.preventDefault();
    if ((parseFloat(anticipoForm.anticipo) || 0) <= 0) { toast.error('Anticipo debe ser mayor a 0'); return; }
    try {
      await axios.post(`${API}/services/${anticipoTarget.id}/movimiento`, {
        tipo_movimiento: 'anticipado',
        nueva_fecha: anticipoTarget.service_date,
        hora_inicio: anticipoTarget.start_time || '09:00',
        hora_fin: anticipoTarget.end_time || '10:00',
        costo_total_cotizado: parseFloat(anticipoForm.costo_total) || 0,
        anticipo: parseFloat(anticipoForm.anticipo) || 0,
        estatus_evento_actual: 'pendiente',
        motivo: `Abono ${anticipoForm.medio_pago}`,
        observaciones_dia_actual: anticipoForm.comentario,
      });
      toast.success('Abono registrado');
      setAnticipoOpen(false);
      fetchServices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const startWork = async (s) => {
    try { await axios.put(`${API}/services/${s.id}`, { status: 'en_proceso' }); toast.success('Trabajo iniciado'); fetchServices(); }
    catch { toast.error('Error'); }
  };
  const confirmWork = async (s) => {
    try { await axios.put(`${API}/services/${s.id}`, { status: 'confirmado' }); toast.success('Confirmado'); fetchServices(); }
    catch { toast.error('Error'); }
  };

  const openFinalize = (s) => {
    setFinalizeTarget(s);
    const acum = parseFloat(s.anticipo_acumulado || s.anticipo || 0);
    const total = parseFloat(s.monto_total || s.quoted_cost || 0);
    setFinalizeForm({ folio_nota: s.folio_nota || '', monto_cobrado: Math.max(0, total - acum), tiene_pendientes: false, pendientes_descripcion: '', comentario_cierre: '' });
    setFinalizeOpen(true);
  };
  const submitFinalize = async (e) => {
    e.preventDefault();
    if (!finalizeForm.folio_nota.trim()) { toast.error('Folio de nota es obligatorio'); return; }
    if (finalizeForm.tiene_pendientes && !finalizeForm.pendientes_descripcion.trim()) { toast.error('Describe qué quedó pendiente'); return; }
    try {
      const cierre = finalizeForm.tiene_pendientes
        ? `⚠️ Finalizado con pendientes: ${finalizeForm.pendientes_descripcion}. ${finalizeForm.comentario_cierre}`
        : finalizeForm.comentario_cierre;
      await axios.post(`${API}/services/${finalizeTarget.id}/finalize`, {
        folio_nota: finalizeForm.folio_nota,
        monto_cobrado: parseFloat(finalizeForm.monto_cobrado) || 0,
        comentario_cierre: cierre,
      });
      toast.success(finalizeForm.tiene_pendientes ? '⚠️ Finalizado con pendientes' : '✅ Trabajo finalizado');
      setFinalizeOpen(false);
      fetchServices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const openCancel = (s) => {
    setCancelTarget(s);
    const acum = parseFloat(s.anticipo_acumulado || s.anticipo || 0);
    setCancelForm({ motivo: '', fecha: todayMx(), comentario: '', devolver: false, monto_devuelto: acum });
    setCancelOpen(true);
  };
  const submitCancel = async (e) => {
    e.preventDefault();
    if (!cancelForm.motivo.trim()) { toast.error('El motivo es obligatorio'); return; }
    try {
      const nota = `⛔ CANCELADO el ${cancelForm.fecha} · Motivo: ${cancelForm.motivo}${cancelForm.devolver ? ` · Anticipo devuelto: $${parseFloat(cancelForm.monto_devuelto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : ''}${cancelForm.comentario ? ' · ' + cancelForm.comentario : ''}`;
      const existing = cancelTarget.internal_comments || '';
      await axios.put(`${API}/services/${cancelTarget.id}`, {
        status: 'cancelado',
        internal_comments: existing ? `${existing}\n${nota}` : nota,
      });
      toast.success('Trabajo cancelado');
      setCancelOpen(false);
      fetchServices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const deleteForever = async (s) => {
    if (!window.confirm(`¿Está seguro de eliminar definitivamente el trabajo ${s.folio}?\nEsta acción NO se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/services/${s.id}`);
      toast.success('Trabajo eliminado definitivamente');
      fetchServices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const openHistory = (s) => { setHistoryTarget(s); setHistoryOpen(true); };
  const openRuta = (g) => { setRutaGroup(g); setRutaOpen(true); };

  // ===== CONVERT disponible -> trabajo (reutiliza AgendaRegisterDialog) =====
  const openConvert = (s) => {
    setConvertTarget(s);
    setConvertOpen(true);
  };

  // Leyendas de apoyo por trabajo (mapa trabajo_id -> {tech_name, start, end})
  const apoyoLegends = useMemo(() => {
    const legend = {};
    // Los "apoyo" tienen apoyo_a_tecnico_id; encontrar el/los trabajos del técnico apoyado en el mismo día+rango
    const apoyos = services.filter(s => (s.record_type || 'trabajo') === 'apoyo' && s.apoyo_a_tecnico_id && s.status !== 'cancelado');
    for (const ap of apoyos) {
      const trabajos = services.filter(t => (t.record_type || 'trabajo') === 'trabajo' && t.service_date === ap.service_date && t.technician_id === ap.apoyo_a_tecnico_id && t.status !== 'cancelado');
      for (const t of trabajos) {
        // solapan?
        const tS = (t.start_time || '').split(':').map(Number);
        const tE = (t.end_time || '').split(':').map(Number);
        const aS = (ap.start_time || '').split(':').map(Number);
        const aE = (ap.end_time || '').split(':').map(Number);
        const tSm = (tS[0] || 0) * 60 + (tS[1] || 0);
        const tEm = (tE[0] || 0) * 60 + (tE[1] || 0);
        const aSm = (aS[0] || 0) * 60 + (aS[1] || 0);
        const aEm = (aE[0] || 0) * 60 + (aE[1] || 0);
        if (tSm < aEm && aSm < tEm) {
          legend[t.id] = { tech_name: ap.technician_name || 'Otro técnico', start: ap.start_time, end: ap.end_time };
        }
      }
    }
    return legend;
  }, [services]);

  const actions = { openEdit, openResched, openAnticipo, confirmWork, startWork, openFinalize, openHistory, openCancel, deleteForever, openConvert };

  // Items activos de la ruta (excluye cancelado/finalizado y sin domicilio)
  const rutaItems = useMemo(() => {
    if (!rutaGroup) return [];
    return rutaGroup.items.filter(x => x.status !== 'cancelado' && x.status !== 'finalizado' && x.address);
  }, [rutaGroup]);

  // URL de Google Maps con waypoints
  const mapsUrl = useMemo(() => {
    if (!rutaItems.length) return '';
    const addrs = rutaItems.map(x => encodeURIComponent(x.address));
    if (addrs.length === 1) return `https://www.google.com/maps/dir/?api=1&destination=${addrs[0]}`;
    const destination = addrs[addrs.length - 1];
    const waypoints = addrs.slice(0, -1).join('|');
    return `https://www.google.com/maps/dir/?api=1&destination=${destination}&waypoints=${waypoints}&travelmode=driving`;
  }, [rutaItems]);

  // Texto para WhatsApp/portapapeles
  const rutaText = useMemo(() => {
    if (!rutaGroup || !rutaItems.length) return '';
    let out = `📅 *Ruta ${rutaGroup.service_date}* · 👷 ${rutaGroup.technician_name}\n`;
    out += `${rutaItems.length} paradas\n\n`;
    rutaItems.forEach((s, i) => {
      out += `*${i + 1}.* ${s.start_time || ''}-${s.end_time || ''}\n`;
      out += `👤 ${s.client_name}\n`;
      out += `📍 ${s.address}\n`;
      const tel = (s.phones || [])[0];
      if (tel) out += `📞 ${tel}\n`;
      if (s.service_type) out += `🔧 ${s.service_type}\n`;
      if (s.description) out += `📝 ${s.description}\n`;
      out += '\n';
    });
    if (mapsUrl) out += `🗺️ Ruta en Google Maps:\n${mapsUrl}`;
    return out;
  }, [rutaGroup, rutaItems, mapsUrl]);

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(rutaText)}`;
    window.open(url, '_blank');
  };
  const copyRuta = async () => {
    try {
      await navigator.clipboard.writeText(rutaText);
      toast.success('Ruta copiada al portapapeles');
    } catch { toast.error('No se pudo copiar'); }
  };
  const printRuta = () => {
    if (!rutaGroup) return;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Permite ventanas emergentes para imprimir'); return; }
    const rows = rutaItems.map((s, i) => `
      <tr>
        <td style="border:1px solid #ccc;padding:6px;text-align:center">${i + 1}</td>
        <td style="border:1px solid #ccc;padding:6px">${s.start_time || ''}-${s.end_time || ''}</td>
        <td style="border:1px solid #ccc;padding:6px"><strong>${s.client_name}</strong><br/><small>${(s.phones || [])[0] || ''}</small></td>
        <td style="border:1px solid #ccc;padding:6px">${s.address}${s.address_reference ? '<br/><small>' + s.address_reference + '</small>' : ''}</td>
        <td style="border:1px solid #ccc;padding:6px">${s.service_type || ''}<br/><small>${s.description || ''}</small></td>
      </tr>`).join('');
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"/><title>Ruta ${rutaGroup.service_date} - ${rutaGroup.technician_name}</title>
      <style>body{font-family:Arial,sans-serif;padding:16px;font-size:12px}h1{font-size:18px;margin:0 0 4px}h2{font-size:13px;margin:0 0 12px;color:#555}table{width:100%;border-collapse:collapse}th{background:#eee;border:1px solid #ccc;padding:6px;text-align:left}</style>
      </head><body>
      <h1>📅 Ruta ${rutaGroup.service_date} · 👷 ${rutaGroup.technician_name}</h1>
      <h2>${rutaItems.length} paradas</h2>
      <table><thead><tr><th>#</th><th>Horario</th><th>Cliente</th><th>Domicilio</th><th>Servicio</th></tr></thead><tbody>${rows}</tbody></table>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const statsByEstado = useMemo(() => {
    const map = { total: services.length };
    ESTADOS.forEach(e => { map[e.value] = services.filter(s => s.status === e.value).length; });
    return map;
  }, [services]);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="agenda-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><CalendarIcon className="h-6 w-6 text-blue-600" />Agenda de Trabajos</h2>
          <p className="text-muted-foreground text-sm">Arrastra para reordenar · Edita cualquier celda haciendo clic</p>
        </div>
        <div className="flex items-center gap-2">
          {undoAvailable && (
            <Button variant="outline" size="sm" onClick={undoLastMove} data-testid="agenda-undo-btn"><Undo2 className="h-4 w-4 mr-1" />Deshacer</Button>
          )}
          <Button onClick={openCreate} data-testid="agenda-new-btn"><Plus className="h-4 w-4 mr-1" />Nuevo trabajo</Button>
        </div>
      </div>

      <Card><CardContent className="p-3 flex flex-wrap gap-2 items-end">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => shiftDay(-1)}>◀</Button>
          <Input type="date" value={filterDate} onChange={(e) => { setFilterDate(e.target.value); setFilterMode('day'); }} className="w-[150px]" data-testid="agenda-date-input" />
          <Button variant="outline" size="sm" onClick={() => shiftDay(1)}>▶</Button>
          <Button variant={filterMode === 'day' ? 'default' : 'outline'} size="sm" onClick={() => { setFilterMode('day'); setFilterDate(todayMx()); }}>Hoy</Button>
          <Button variant={filterMode === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilterMode('all')}>Todos</Button>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Técnico</Label>
          <Select value={filterTec} onValueChange={setFilterTec}>
            <SelectTrigger className="w-[160px]" data-testid="agenda-tec-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {technicians.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Estado</Label>
          <Select value={filterEst} onValueChange={setFilterEst}>
            <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {ESTADOS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Tipo</Label>
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger className="w-[150px]" data-testid="agenda-tipo-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label className="text-xs">Servicio</Label><Input value={filterService} onChange={(e) => setFilterService(e.target.value)} placeholder="Ej: plomería" className="w-[140px]" /></div>
        <div className="space-y-1 flex-1 min-w-[180px]">
          <Label className="text-xs">Buscar (cliente, tel, folio)</Label>
          <div className="relative"><Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" /><Input value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} className="pl-8" data-testid="agenda-search" /></div>
        </div>
        {(filterEst !== 'all' || filterTec !== 'all' || filterTipo !== 'all' || filterService || filterSearch) && <Button variant="ghost" size="sm" onClick={() => { setFilterEst('all'); setFilterTec('all'); setFilterTipo('all'); setFilterService(''); setFilterSearch(''); }}><X className="h-4 w-4 mr-1" />Limpiar</Button>}
      </CardContent></Card>

      <div className="flex flex-wrap gap-2">
        <Badge variant="outline" className="text-xs">Total: <span className="ml-1 font-bold">{statsByEstado.total}</span></Badge>
        {ESTADOS.map(e => <Badge key={e.value} className={`text-xs cursor-pointer ${e.cell}`} onClick={() => setFilterEst(filterEst === e.value ? 'all' : e.value)}>{e.label}: <span className="ml-1 font-bold">{statsByEstado[e.value] || 0}</span></Badge>)}
      </div>

      <Card><CardContent className="p-0 overflow-x-auto">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table className="w-full text-xs">
            <thead className="bg-muted/60"><tr className="text-[11px]">
              <th className="w-6"></th>
              <th className="px-2 py-2 text-left">Folio</th>
              <th className="px-2 py-2 text-left">Fecha</th>
              <th className="px-2 py-2 text-left">Horario</th>
              <th className="px-2 py-2 text-left">Técnico</th>
              <th className="px-2 py-2 text-left">Asignó</th>
              <th className="px-2 py-2 text-left">Servicio</th>
              <th className="px-2 py-2 text-left">Cliente</th>
              <th className="px-2 py-2 text-left">Teléfono</th>
              <th className="px-2 py-2 text-left">Domicilio</th>
              <th className="px-2 py-2 text-left">Medio</th>
              <th className="px-2 py-2 text-right">Costo</th>
              <th className="px-2 py-2 text-right">Abonos</th>
              <th className="px-2 py-2 text-right">Restante</th>
              <th className="px-2 py-2 text-left">Estado</th>
              <th className="px-2 py-2 text-left">Folio nota</th>
              <th className="px-2 py-2 text-left">Comentarios</th>
              <th className="w-10"></th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={18} className="text-center py-8 text-muted-foreground">Cargando...</td></tr>
                : services.length === 0 ? <tr><td colSpan={18} className="text-center py-8 text-muted-foreground">Sin trabajos en el filtro seleccionado</td></tr>
                : groups.map(g => (
                  <AgendaGroup key={g.key} group={g} technicians={technicians} expanded={expanded} toggleExpand={toggleExpand} onFieldSave={onFieldSave} actions={actions} isAdmin={isAdmin} onOpenRuta={openRuta} apoyoLegends={apoyoLegends} />
                ))}
            </tbody>
          </table>
        </DndContext>
      </CardContent></Card>

      {/* CREATE — usa el nuevo modal con selector de tipo */}
      <AgendaRegisterDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={fetchServices} />

      {/* EDIT */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar {editTarget?.folio}</DialogTitle></DialogHeader>
          {editTarget && (
            <form onSubmit={submitEdit} className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Fecha</Label><Input type="date" value={editTarget.service_date || ''} onChange={(e) => setEditTarget({ ...editTarget, service_date: e.target.value })} /></div>
                <div><Label className="text-xs">Llegada</Label><Input type="time" value={editTarget.start_time || ''} onChange={(e) => setEditTarget({ ...editTarget, start_time: e.target.value })} /></div>
                <div><Label className="text-xs">Salida</Label><Input type="time" value={editTarget.end_time || ''} onChange={(e) => setEditTarget({ ...editTarget, end_time: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Cliente</Label><Input value={editTarget.client_name || ''} onChange={(e) => setEditTarget({ ...editTarget, client_name: e.target.value })} /></div>
                <div><Label className="text-xs">Teléfono</Label><Input value={(editTarget.phones || [''])[0]} onChange={(e) => setEditTarget({ ...editTarget, phones: [e.target.value] })} /></div>
              </div>
              <div><Label className="text-xs">Domicilio</Label><Input value={editTarget.address || ''} onChange={(e) => setEditTarget({ ...editTarget, address: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Servicio</Label><Input value={editTarget.service_type || ''} onChange={(e) => setEditTarget({ ...editTarget, service_type: e.target.value })} /></div>
                <div><Label className="text-xs">Medio contacto</Label><Input value={editTarget.contact_medium || ''} onChange={(e) => setEditTarget({ ...editTarget, contact_medium: e.target.value })} /></div>
              </div>
              <div><Label className="text-xs">Técnico</Label>
                <select className="flex h-9 w-full rounded-md border bg-background px-3 py-1 text-sm" value={editTarget.technician_id || ''} onChange={(e) => { const t = technicians.find(x => x.id === e.target.value); setEditTarget({ ...editTarget, technician_id: e.target.value, technician_name: t?.name || '' }); }}>
                  <option value="">— Sin asignar —</option>
                  {technicians.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Costo total</Label><Input type="number" value={editTarget.quoted_cost || 0} onChange={(e) => setEditTarget({ ...editTarget, quoted_cost: parseFloat(e.target.value) || 0 })} /></div>
                <div><Label className="text-xs">Estado</Label>
                  <Select value={editTarget.status || 'pendiente'} onValueChange={(v) => setEditTarget({ ...editTarget, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{ESTADOS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs">Comentarios</Label><Textarea value={editTarget.internal_comments || ''} onChange={(e) => setEditTarget({ ...editTarget, internal_comments: e.target.value })} rows={2} /></div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button><Button type="submit">Guardar</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* RESCHEDULE */}
      <Dialog open={reschedOpen} onOpenChange={setReschedOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reagendar {reschedTarget?.folio}</DialogTitle>
            <DialogDescription>Cliente: <strong>{reschedTarget?.client_name}</strong> · Se conservará el registro del día original ({reschedTarget?.service_date}) con la leyenda &quot;Reagendado para [nueva fecha]&quot;.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitResched} className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Nueva fecha *</Label><Input type="date" value={reschedForm.nueva_fecha} onChange={(e) => setReschedForm({ ...reschedForm, nueva_fecha: e.target.value })} required data-testid="agenda-resched-date" /></div>
              <div><Label className="text-xs">Llegada</Label><Input type="time" value={reschedForm.hora_inicio} onChange={(e) => setReschedForm({ ...reschedForm, hora_inicio: e.target.value })} /></div>
              <div><Label className="text-xs">Salida</Label><Input type="time" value={reschedForm.hora_fin} onChange={(e) => setReschedForm({ ...reschedForm, hora_fin: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs">Técnico asignado</Label>
              <select className="flex h-9 w-full rounded-md border bg-background px-3 py-1 text-sm" value={reschedForm.nuevo_tecnico_id} onChange={(e) => setReschedForm({ ...reschedForm, nuevo_tecnico_id: e.target.value })}>
                <option value="">— Mantener {reschedTarget?.technician_name || 'el mismo'} —</option>
                {technicians.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Servicio</Label><Input value={reschedForm.service_type} onChange={(e) => setReschedForm({ ...reschedForm, service_type: e.target.value })} /></div>
              <div><Label className="text-xs">Motivo de la reagenda</Label><Input value={reschedForm.motivo} onChange={(e) => setReschedForm({ ...reschedForm, motivo: e.target.value })} placeholder="Cliente no estaba, lluvia..." /></div>
            </div>
            <div><Label className="text-xs">Descripción completa del trabajo</Label><Textarea value={reschedForm.description} onChange={(e) => setReschedForm({ ...reschedForm, description: e.target.value })} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Costo total</Label><Input type="number" value={reschedForm.costo_total} onChange={(e) => setReschedForm({ ...reschedForm, costo_total: e.target.value })} /></div>
              <div><Label className="text-xs">Anticipo adicional</Label><Input type="number" value={reschedForm.anticipo_extra} onChange={(e) => setReschedForm({ ...reschedForm, anticipo_extra: e.target.value })} /></div>
            </div>
            <div className="rounded-md border bg-blue-50 dark:bg-blue-950/20 p-2 text-xs space-y-0.5">
              <div className="flex justify-between"><span className="text-muted-foreground">Anticipos previos</span><span className="font-medium text-emerald-600">{fmt(reschedForm.anticipo_actual)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">+ Anticipo hoy</span><span className="font-medium text-emerald-600">{fmt(reschedForm.anticipo_extra)}</span></div>
              <div className="flex justify-between border-t pt-0.5"><span className="font-semibold">Total abonos</span><span className="font-bold text-emerald-700">{fmt((parseFloat(reschedForm.anticipo_actual) || 0) + (parseFloat(reschedForm.anticipo_extra) || 0))}</span></div>
              <div className="flex justify-between"><span className="font-semibold">Cantidad restante</span><span className="font-bold text-orange-600">{fmt(Math.max(0, (parseFloat(reschedForm.costo_total) || 0) - (parseFloat(reschedForm.anticipo_actual) || 0) - (parseFloat(reschedForm.anticipo_extra) || 0)))}</span></div>
            </div>
            <div><Label className="text-xs">Pendientes por realizar</Label><Textarea value={reschedForm.pendientes} onChange={(e) => setReschedForm({ ...reschedForm, pendientes: e.target.value })} rows={2} /></div>
            <div><Label className="text-xs">Comentarios / ¿Qué pasó hoy?</Label><Textarea value={reschedForm.observaciones_dia_actual} onChange={(e) => setReschedForm({ ...reschedForm, observaciones_dia_actual: e.target.value })} rows={2} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setReschedOpen(false)}>Cancelar</Button><Button type="submit" data-testid="agenda-resched-submit">Reagendar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ANTICIPO */}
      <Dialog open={anticipoOpen} onOpenChange={setAnticipoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar abono {anticipoTarget?.folio}</DialogTitle>
            <DialogDescription>Anticipos previos: <span className="font-bold text-emerald-600">{fmt(anticipoTarget?.anticipo_acumulado || anticipoTarget?.anticipo || 0)}</span></DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAnticipo} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Costo total</Label><Input type="number" value={anticipoForm.costo_total} onChange={(e) => setAnticipoForm({ ...anticipoForm, costo_total: e.target.value })} /></div>
              <div><Label className="text-xs">Anticipo *</Label><Input type="number" min="0.01" step="0.01" value={anticipoForm.anticipo} onChange={(e) => setAnticipoForm({ ...anticipoForm, anticipo: e.target.value })} required data-testid="agenda-anticipo-monto" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Fecha</Label><Input type="date" value={anticipoForm.fecha} onChange={(e) => setAnticipoForm({ ...anticipoForm, fecha: e.target.value })} /></div>
              <div><Label className="text-xs">Medio de pago</Label>
                <Select value={anticipoForm.medio_pago} onValueChange={(v) => setAnticipoForm({ ...anticipoForm, medio_pago: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Comentario</Label><Input value={anticipoForm.comentario} onChange={(e) => setAnticipoForm({ ...anticipoForm, comentario: e.target.value })} /></div>
            {anticipoTarget && (
              <div className="rounded-md bg-blue-50 dark:bg-blue-950/20 p-2 text-xs">
                <div className="flex justify-between"><span>Nuevo acumulado</span><span className="font-bold text-emerald-600">{fmt((parseFloat(anticipoTarget.anticipo_acumulado || anticipoTarget.anticipo || 0)) + (parseFloat(anticipoForm.anticipo) || 0))}</span></div>
                <div className="flex justify-between"><span>Restante</span><span className="font-bold text-orange-600">{fmt(Math.max(0, (parseFloat(anticipoForm.costo_total) || 0) - (parseFloat(anticipoTarget.anticipo_acumulado || anticipoTarget.anticipo || 0)) - (parseFloat(anticipoForm.anticipo) || 0)))}</span></div>
              </div>
            )}
            <DialogFooter><Button type="button" variant="outline" onClick={() => setAnticipoOpen(false)}>Cancelar</Button><Button type="submit" data-testid="agenda-anticipo-submit">Guardar abono</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* FINALIZE */}
      <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Finalizar {finalizeTarget?.folio}</DialogTitle>
            <DialogDescription>
              Costo cotizado: {fmt(finalizeTarget?.monto_total || finalizeTarget?.quoted_cost || 0)} · Anticipos: <span className="text-emerald-600 font-medium">{fmt(finalizeTarget?.anticipo_acumulado || finalizeTarget?.anticipo || 0)}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitFinalize} className="space-y-3">
            <div><Label className="text-xs">Folio de nota *</Label><Input value={finalizeForm.folio_nota} onChange={(e) => setFinalizeForm({ ...finalizeForm, folio_nota: e.target.value })} required data-testid="agenda-final-folio" /></div>
            <div><Label className="text-xs">Total cobrado hoy *</Label><Input type="number" min="0" step="0.01" value={finalizeForm.monto_cobrado} onChange={(e) => setFinalizeForm({ ...finalizeForm, monto_cobrado: e.target.value })} required /></div>
            <div className="rounded-md border p-3 space-y-2">
              <Label className="text-xs">¿Quedó algún trabajo pendiente?</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={finalizeForm.tiene_pendientes ? 'default' : 'outline'} onClick={() => setFinalizeForm({ ...finalizeForm, tiene_pendientes: true })}>Sí</Button>
                <Button type="button" size="sm" variant={!finalizeForm.tiene_pendientes ? 'default' : 'outline'} onClick={() => setFinalizeForm({ ...finalizeForm, tiene_pendientes: false, pendientes_descripcion: '' })}>No</Button>
              </div>
              {finalizeForm.tiene_pendientes && (
                <div><Label className="text-xs">Describe lo pendiente *</Label><Textarea value={finalizeForm.pendientes_descripcion} onChange={(e) => setFinalizeForm({ ...finalizeForm, pendientes_descripcion: e.target.value })} rows={2} required /></div>
              )}
            </div>
            <div><Label className="text-xs">Comentarios finales</Label><Textarea value={finalizeForm.comentario_cierre} onChange={(e) => setFinalizeForm({ ...finalizeForm, comentario_cierre: e.target.value })} rows={2} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setFinalizeOpen(false)}>Cancelar</Button><Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="agenda-final-submit">Finalizar trabajo</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* HISTORY */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Historial {historyTarget?.folio}</DialogTitle></DialogHeader>
          {historyTarget && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Datos actuales</p>
                <p><b>Cliente:</b> {historyTarget.client_name} · <b>Servicio:</b> {historyTarget.service_type}</p>
                <p><b>Fecha actual:</b> {historyTarget.service_date} {historyTarget.start_time}-{historyTarget.end_time}</p>
                <p><b>Técnico:</b> {historyTarget.technician_name || '-'}</p>
                <p><b>Costo:</b> {fmt(historyTarget.monto_total || historyTarget.quoted_cost || 0)} · <b>Anticipos:</b> {fmt(historyTarget.anticipo_acumulado || historyTarget.anticipo || 0)}</p>
              </div>

              {historyTarget.eventos && historyTarget.eventos.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm mb-2">📅 Eventos / Reagendados ({historyTarget.eventos.length})</h4>
                  <div className="space-y-2">
                    {historyTarget.eventos.map((ev, i) => (
                      <div key={i} className="rounded border p-2 text-xs bg-muted/30">
                        <div className="flex justify-between">
                          <span><b>#{ev.numero_evento || (i + 1)}</b> · {ev.fecha_programada || ev.fecha} {ev.hora_inicio}-{ev.hora_fin}</span>
                          <span className="text-purple-700 capitalize">{ev.estatus || '-'}</span>
                        </div>
                        <p className="text-muted-foreground">Técnico: {ev.tecnico_nombre || '-'}</p>
                        {ev.motivo && <p><b>Motivo:</b> {ev.motivo}</p>}
                        {ev.observaciones && <p className="text-muted-foreground">{ev.observaciones}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {historyTarget.historial_asignaciones && historyTarget.historial_asignaciones.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm mb-2">🔄 Movimientos</h4>
                  <div className="space-y-2">
                    {historyTarget.historial_asignaciones.map((h, i) => (
                      <div key={i} className="rounded border p-2 text-xs bg-muted/30">
                        <p><b>{h.tipo || h.accion || 'Movimiento'}</b> · {h.fecha_movimiento || h.created_at?.slice(0, 10)}</p>
                        {h.motivo && <p className="text-muted-foreground">{h.motivo}</p>}
                        {h.anticipo && <p className="text-emerald-600">Anticipo: {fmt(h.anticipo)}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {historyTarget.folio_nota && (
                <div className="rounded-md border bg-emerald-50/40 dark:bg-emerald-950/20 p-3">
                  <p className="text-xs text-emerald-700 font-semibold">✅ Cierre</p>
                  <p><b>Folio nota:</b> {historyTarget.folio_nota}</p>
                  <p><b>Total cobrado:</b> {fmt(historyTarget.monto_cobrado)}</p>
                  {historyTarget.comentario_cierre && <p><b>Comentario:</b> {historyTarget.comentario_cierre}</p>}
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setHistoryOpen(false)}>Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CANCEL */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancelar trabajo {cancelTarget?.folio}</DialogTitle>
            <DialogDescription>Cliente: <strong>{cancelTarget?.client_name}</strong>. El trabajo se marcará como Cancelado pero se conservará en el historial.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCancel} className="space-y-3">
            <div><Label className="text-xs">Motivo de cancelación *</Label><Input value={cancelForm.motivo} onChange={(e) => setCancelForm({ ...cancelForm, motivo: e.target.value })} required placeholder="Ej: Cliente ya no quiere el servicio" data-testid="agenda-cancel-motivo" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Fecha de cancelación</Label><Input type="date" value={cancelForm.fecha} onChange={(e) => setCancelForm({ ...cancelForm, fecha: e.target.value })} /></div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={cancelForm.devolver} onChange={(e) => setCancelForm({ ...cancelForm, devolver: e.target.checked })} />
                  ¿Devolver anticipo?
                </label>
              </div>
            </div>
            {cancelForm.devolver && (
              <div><Label className="text-xs">Cantidad devuelta</Label><Input type="number" min="0" step="0.01" value={cancelForm.monto_devuelto} onChange={(e) => setCancelForm({ ...cancelForm, monto_devuelto: e.target.value })} /></div>
            )}
            <div><Label className="text-xs">Comentario (opcional)</Label><Textarea value={cancelForm.comentario} onChange={(e) => setCancelForm({ ...cancelForm, comentario: e.target.value })} rows={2} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setCancelOpen(false)}>Volver</Button><Button type="submit" variant="destructive" data-testid="agenda-cancel-submit">Cancelar trabajo</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CONFLICT DETAIL DIALOG */}
      <Dialog open={!!conflictDialog} onOpenChange={(o) => { if (!o) setConflictDialog(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="conflict-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700 dark:text-red-300"><Ban className="h-5 w-5" />Horario no disponible</DialogTitle>
            <DialogDescription>
              {conflictDialog?.movedItem && (
                <>El registro <strong>{conflictDialog.movedItem.folio}</strong> ({conflictDialog.movedItem.client_name || conflictDialog.movedItem.motivo_interno || conflictDialog.movedItem.record_type}) no puede quedar de {conflictDialog.movedItem.start_time} a {conflictDialog.movedItem.end_time} para {conflictDialog.movedItem.technician_name} el {conflictDialog.movedItem.service_date}.</>
              )}
            </DialogDescription>
          </DialogHeader>
          {conflictDialog && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border-2 border-red-500 bg-red-50 dark:bg-red-950/20 p-3">
                <p className="font-semibold text-red-800 dark:text-red-200 text-xs mb-1">Registro que causa el conflicto:</p>
                <div className="text-xs">
                  <strong>{conflictDialog.conflict.conflict_client || conflictDialog.conflict.conflict_folio}</strong> ·
                  {' '}{conflictDialog.conflict.conflict_start}-{conflictDialog.conflict.conflict_end}
                  {conflictDialog.conflict.conflict_folio && <> · Folio {conflictDialog.conflict.conflict_folio}</>}
                </div>
              </div>
              {conflictDialog.slots.length > 0 ? (
                <div>
                  <p className="font-semibold text-xs mb-1 text-emerald-800 dark:text-emerald-200">Horarios disponibles (click para aplicar):</p>
                  <div className="grid grid-cols-2 gap-2">
                    {conflictDialog.slots.map((slot, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={async () => {
                          const dur = conflictDialog.movedItem.duration_minutes || 60;
                          const slotDur = (() => { const s = slot.start_time.split(':').map(Number); const e = slot.end_time.split(':').map(Number); return (e[0] * 60 + e[1]) - (s[0] * 60 + s[1]); })();
                          const useDur = Math.min(dur, slotDur);
                          const st = slot.start_time;
                          const [h, m] = st.split(':').map(Number);
                          const endMin = h * 60 + m + useDur;
                          const et = `${String(Math.floor(endMin / 60)).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
                          try {
                            await axios.put(`${API}/services/${conflictDialog.movedItem.id}`, { start_time: st, end_time: et, duration_minutes: useDur });
                            toast.success(`Horario aplicado: ${st}-${et}`);
                            setConflictDialog(null);
                            fetchServices();
                          } catch (err) {
                            toast.error(err.response?.data?.detail?.message || 'No se pudo aplicar');
                          }
                        }}
                        className="rounded border bg-emerald-100 hover:bg-emerald-200 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200 px-2 py-1.5 text-xs text-left"
                        data-testid={`conflict-slot-${i}`}
                      >
                        <div className="font-semibold">{slot.start_time}-{slot.end_time}</div>
                        <div className="text-[10px] opacity-80">{slot.duration_min} min disponibles</div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No hay huecos libres en el horario de negocio para este técnico este día.</p>
              )}
              {conflictDialog.ocupados.length > 0 && (
                <div>
                  <p className="font-semibold text-xs mb-1 text-muted-foreground">Ocupados en el día:</p>
                  <div className="space-y-1">
                    {conflictDialog.ocupados.map((o, i) => (
                      <div key={i} className="text-xs text-muted-foreground flex justify-between border-b py-0.5">
                        <span>{o.start_time}-{o.end_time}</span>
                        <span>{o.client_name || o.motivo_interno || o.record_type} · {o.folio}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setConflictDialog(null)} data-testid="conflict-close">Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CONVERT disponible -> trabajo — reutiliza AgendaRegisterDialog */}
      <AgendaRegisterDialog
        open={convertOpen}
        onOpenChange={setConvertOpen}
        onCreated={fetchServices}
        convertFromId={convertTarget?.id}
        forceType="trabajo"
        skipTypeStep
        initialData={convertTarget ? {
          record_type: 'trabajo',
          service_date: convertTarget.service_date,
          start_time: convertTarget.start_time,
          end_time: convertTarget.end_time,
          technician_id: convertTarget.technician_id || '',
          description: convertTarget.description || '',
        } : null}
      />

      {/* RUTA DEL DÍA */}
      <Dialog open={rutaOpen} onOpenChange={setRutaOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-blue-600" />Ruta del día</DialogTitle>
            <DialogDescription>
              {rutaGroup?.service_date} · <strong>{rutaGroup?.technician_name}</strong> · {rutaItems.length} paradas
            </DialogDescription>
          </DialogHeader>
          {rutaItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay trabajos activos con domicilio en este día/técnico.</p>
          ) : (
            <div className="space-y-2 text-sm">
              {rutaItems.map((s, i) => (
                <div key={s.id} className="rounded-md border p-3 flex gap-3 items-start" data-testid={`ruta-item-${i}`}>
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">{i + 1}</div>
                  <div className="flex-1 space-y-0.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-semibold">{s.client_name}</span>
                      <span className="text-xs text-muted-foreground">{s.start_time || ''}-{s.end_time || ''}</span>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{s.address}{s.address_reference && ` · ${s.address_reference}`}</div>
                    {(s.phones || [])[0] && <div className="text-xs">📞 {s.phones[0]}</div>}
                    {s.service_type && <div className="text-xs"><strong>{s.service_type}</strong>{s.description ? ` — ${s.description}` : ''}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={() => setRutaOpen(false)}>Cerrar</Button>
            {rutaItems.length > 0 && (
              <>
                <Button type="button" variant="outline" onClick={copyRuta} data-testid="ruta-copy-btn"><Copy className="h-4 w-4 mr-1" />Copiar</Button>
                <Button type="button" variant="outline" onClick={printRuta} data-testid="ruta-print-btn"><Printer className="h-4 w-4 mr-1" />Imprimir</Button>
                <Button type="button" variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300" onClick={shareWhatsApp} data-testid="ruta-whatsapp-btn"><MessageCircle className="h-4 w-4 mr-1" />WhatsApp</Button>
                <Button type="button" onClick={() => window.open(mapsUrl, '_blank')} data-testid="ruta-maps-btn"><MapPin className="h-4 w-4 mr-1" />Abrir en Maps</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
